import { useState, useEffect, useCallback } from "react";
import { API, getAuthHeaders, authFetch } from "@/App";
import { toast } from "sonner";

const printKeys = (list) => list.filter(a => a.print_with_sheet);

export function useAttachments({ orders, setOrders, setAttachmentCounts, setEmailCounts, commentCounts, setCommentCounts, onFabricPhotosChanged }) {
  const [attachmentModal, setAttachmentModal] = useState({ open: false, orderId: null, orderName: "" });
  const [emailModal, setEmailModal] = useState({ open: false, orderId: null, orderName: "" });
  const [attachments, setAttachments] = useState([]);
  const [emails, setEmails] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: "", content: "", sender: "" });
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [detectedAttachments, setDetectedAttachments] = useState([]);
  const [previewModal, setPreviewModal] = useState({ open: false, type: null, url: null, filename: null });
  const [commentsModal, setCommentsModal] = useState({ open: false, orderId: null, orderName: "" });

  // Load comment counts when orders change
  const loadCommentCounts = useCallback(async () => {
    try {
      const counts = {};
      for (const order of orders) {
        const res = await authFetch(`${API}/orders/${order.order_id}/comments/count`, { headers: getAuthHeaders() });
        if (res.ok) { const data = await res.json(); counts[order.order_id] = data.count || 0; }
      }
      setCommentCounts(counts);
    } catch (e) { console.error("Failed to load comment counts:", e); }
  }, [orders, setCommentCounts]);

  useEffect(() => {
    if (orders.length > 0) loadCommentCounts();
  }, [orders.length]);

  const openAttachmentModal = useCallback(async (orderId, orderName) => {
    setAttachmentModal({ open: true, orderId, orderName });
    try {
      const res = await authFetch(`${API}/orders/${orderId}/attachments`, { headers: getAuthHeaders() });
      if (res.ok) { const data = await res.json(); setAttachments(data.attachments || []); }
    } catch (e) { console.error("Failed to load attachments:", e); }
  }, []);

  const closeAttachmentModal = useCallback(() => {
    setAttachmentModal({ open: false, orderId: null, orderName: "" });
    setAttachments([]);
  }, []);

  const handleFileUpload = useCallback(async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setIsUploading(true);
    const orderId = attachmentModal.orderId;
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await authFetch(`${API}/orders/${orderId}/attachments`, { method: "POST", body: formData });
        if (res.ok) {
          const data = await res.json();
          setAttachments(prev => [...prev, data.attachment]);
          setAttachmentCounts(prev => ({ ...prev, [orderId]: (prev[orderId] || 0) + 1 }));
          toast.success(`${file.name} uploadé`);
        } else { const err = await res.json(); toast.error(err.detail || "Erreur lors de l'upload"); }
      } catch { toast.error(`Erreur: ${file.name}`); }
    }
    setIsUploading(false);
    e.target.value = '';
  }, [attachmentModal.orderId, setAttachmentCounts]);

  const handleDeleteAttachment = useCallback(async (attachmentId) => {
    const orderId = attachmentModal.orderId;
    try {
      const res = await authFetch(`${API}/orders/${orderId}/attachments/${attachmentId}`, { method: "DELETE" });
      if (res.ok) {
        const remaining = attachments.filter(a => a.attachment_id !== attachmentId);
        setAttachments(remaining);
        setAttachmentCounts(prev => ({ ...prev, [orderId]: Math.max((prev[orderId] || 1) - 1, 0) }));
        onFabricPhotosChanged?.(orderId, printKeys(remaining));
        toast.success("Pièce jointe supprimée");
      }
    } catch { toast.error("Erreur lors de la suppression"); }
  }, [attachmentModal.orderId, attachments, setAttachmentCounts, onFabricPhotosChanged]);

  const handleTogglePrintWithSheet = useCallback(async (attachment) => {
    const orderId = attachmentModal.orderId;
    const print = !attachment.print_with_sheet;
    try {
      const res = await authFetch(`${API}/orders/${orderId}/attachments/${attachment.attachment_id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ print_with_sheet: print }),
      });
      if (!res.ok) { const err = await res.json().catch(() => null); toast.error(err?.detail || "Erreur"); return; }
      const next = attachments.map(a => a.attachment_id === attachment.attachment_id ? { ...a, print_with_sheet: print } : a);
      setAttachments(next);
      onFabricPhotosChanged?.(orderId, printKeys(next));
      toast.success(print ? "Image imprimée avec la fiche de production" : "Image retirée de la fiche de production");
    } catch { toast.error("Erreur réseau"); }
  }, [attachmentModal.orderId, attachments, onFabricPhotosChanged]);

  const downloadAttachment = useCallback((orderId, attachmentId) => {
    window.open(`${API}/orders/${orderId}/attachments/${attachmentId}/download`, '_blank');
  }, []);

  const previewAttachment = useCallback((orderId, attachment) => {
    const previewableImageTypes = ['.png', '.jpg', '.jpeg', '.gif', '.webp'];
    const fileType = attachment.file_type.toLowerCase();
    if (previewableImageTypes.includes(fileType)) {
      setPreviewModal({ open: true, type: 'image', url: `${API}/orders/${orderId}/attachments/${attachment.attachment_id}/preview`, filename: attachment.original_filename });
    } else if (fileType === '.pdf') {
      setPreviewModal({ open: true, type: 'pdf', url: `${API}/orders/${orderId}/attachments/${attachment.attachment_id}/preview`, filename: attachment.original_filename });
    } else {
      downloadAttachment(orderId, attachment.attachment_id);
    }
  }, [downloadAttachment]);

  const closePreviewModal = useCallback(() => {
    setPreviewModal({ open: false, type: null, url: null, filename: null });
  }, []);

  const openEmailModal = useCallback(async (orderId, orderName) => {
    setEmailModal({ open: true, orderId, orderName });
    setEmailForm({ subject: "", content: "", sender: "" });
    try {
      const res = await authFetch(`${API}/orders/${orderId}/emails`, { headers: getAuthHeaders() });
      if (res.ok) { const data = await res.json(); setEmails(data.emails || []); }
    } catch (e) { console.error("Failed to load emails:", e); }
  }, []);

  const closeEmailModal = useCallback(() => {
    setEmailModal({ open: false, orderId: null, orderName: "" });
    setEmails([]);
    setEmailForm({ subject: "", content: "", sender: "" });
  }, []);

  const openCommentsModal = useCallback((orderId, orderName) => {
    setCommentsModal({ open: true, orderId, orderName });
  }, []);

  const closeCommentsModal = useCallback(() => {
    setCommentsModal({ open: false, orderId: null, orderName: "" });
    loadCommentCounts();
  }, [loadCommentCounts]);

  const detectAttachmentMentions = useCallback((content) => {
    const patterns = [
      /pièce[s]?\s*jointe[s]?/gi, /ci-joint[e]?[s]?/gi, /en\s*(?:pièce[s]?\s*)?attaché[e]?[s]?/gi,
      /vous\s*trouverez\s*(?:ci-)?joint[e]?[s]?/gi, /document[s]?\s*(?:ci-)?joint[s]?/gi,
      /fichier[s]?\s*(?:ci-)?joint[s]?/gi, /joint[e]?[s]?\s*à\s*(?:ce|cet)\s*(?:e-?mail|courriel|message)/gi,
      /\.pdf\b/gi, /\.xlsx?\b/gi, /\.docx?\b/gi, /\.png\b/gi, /\.jpe?g\b/gi,
      /attach(?:ed|ment)[s]?/gi, /enclosed/gi,
      /(?:facture|devis|bon\s*de\s*commande|bdc|bl|bon\s*de\s*livraison)[\s_-]?\d*/gi,
    ];
    const mentions = [];
    for (const pattern of patterns) { const matches = content.match(pattern); if (matches) mentions.push(...matches); }
    return [...new Set(mentions.map(m => m.toLowerCase()))].slice(0, 5);
  }, []);

  const handleEmailContentChange = useCallback((content) => {
    setEmailForm(prev => ({ ...prev, content }));
    setDetectedAttachments(detectAttachmentMentions(content));
  }, [detectAttachmentMentions]);

  const handleEmailAttachmentSelect = useCallback((e) => {
    setEmailAttachments(prev => [...prev, ...Array.from(e.target.files)]);
  }, []);

  const removeEmailAttachment = useCallback((index) => {
    setEmailAttachments(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleAddEmail = useCallback(async () => {
    if (!emailForm.content.trim()) { toast.error("Le contenu de l'email est requis"); return; }
    const orderId = emailModal.orderId;
    setIsUploading(true);
    try {
      const res = await authFetch(`${API}/orders/${orderId}/emails`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...emailForm, has_attachments: emailAttachments.length > 0, attachment_count: emailAttachments.length }),
      });
      if (res.ok) {
        const data = await res.json();
        const newEmail = data.email;
        const updatedFields = data.updated_fields || {};
        setEmails(prev => [newEmail, ...prev]);
        setEmailCounts(prev => ({ ...prev, [orderId]: (prev[orderId] || 0) + 1 }));
        if (Object.keys(updatedFields).length > 0) {
          setOrders(prev => prev.map(order => order.order_id === orderId ? { ...order, data: { ...order.data, ...updatedFields } } : order));
        }
        if (emailAttachments.length > 0) {
          let uploadedCount = 0;
          for (const file of emailAttachments) {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("email_id", newEmail.email_id);
            const uploadRes = await authFetch(`${API}/orders/${orderId}/attachments`, { method: "POST", body: formData });
            if (uploadRes.ok) { uploadedCount++; setAttachmentCounts(prev => ({ ...prev, [orderId]: (prev[orderId] || 0) + 1 })); }
          }
          toast.success(uploadedCount > 0 ? `Email ajouté avec ${uploadedCount} pièce${uploadedCount > 1 ? 's' : ''} jointe${uploadedCount > 1 ? 's' : ''}` : "Email ajouté (erreur upload PJ)");
        } else {
          const fieldCount = Object.keys(updatedFields).length;
          toast.success(fieldCount > 0 ? `Email ajouté \u2014 ${fieldCount} champ${fieldCount > 1 ? 's' : ''} mis à jour : ${Object.keys(updatedFields).join(", ")}` : "Email ajouté");
        }
        setEmailForm({ subject: "", content: "", sender: "" });
        setEmailAttachments([]);
        setDetectedAttachments([]);
      } else { const errorData = await res.json().catch(() => null); toast.error(errorData?.detail || `Erreur ${res.status}`); }
    } catch (e) { toast.error("Erreur réseau lors de l'ajout de l'email"); }
    finally { setIsUploading(false); }
  }, [emailForm, emailAttachments, emailModal.orderId, setOrders, setEmailCounts, setAttachmentCounts]);

  const handleDeleteEmail = useCallback(async (emailId) => {
    const orderId = emailModal.orderId;
    try {
      const res = await authFetch(`${API}/orders/${orderId}/emails/${emailId}`, { method: "DELETE" });
      if (res.ok) {
        setEmails(prev => prev.filter(e => e.email_id !== emailId));
        setEmailCounts(prev => ({ ...prev, [orderId]: Math.max((prev[orderId] || 1) - 1, 0) }));
        toast.success("Email supprimé");
      }
    } catch { toast.error("Erreur lors de la suppression"); }
  }, [emailModal.orderId, setEmailCounts]);

  return {
    attachmentModal, emailModal, attachments, emails, isUploading, emailForm, setEmailForm,
    emailAttachments, detectedAttachments, previewModal, commentsModal,
    openAttachmentModal, closeAttachmentModal, handleFileUpload, handleDeleteAttachment,
    downloadAttachment, previewAttachment, closePreviewModal,
    openEmailModal, closeEmailModal, openCommentsModal, closeCommentsModal,
    loadCommentCounts, handleEmailContentChange, handleEmailAttachmentSelect,
    removeEmailAttachment, handleAddEmail, handleDeleteEmail, handleTogglePrintWithSheet,
  };
}
